/**
 * Formats Firebase auth errors for display, especially password policy errors.
 *
 * Firebase returns auth/password-does-not-meet-requirements with a message like:
 * "Firebase: Missing password requirements: [Item1, Item2, Item3] (auth/...)"
 * This helper parses that and returns a user-friendly bullet list.
 */

const PASSWORD_REQUIREMENTS_CODE = 'auth/password-does-not-meet-requirements';
const REQUIREMENTS_LIST_REGEX = /Missing password requirements:\s*\[([^\]]+)\]/;

/**
 * If the error is Firebase's password-does-not-meet-requirements, returns a
 * user-friendly message listing the missing requirements. Otherwise returns undefined.
 */
export function formatPasswordRequirementsError(error: unknown): string | undefined {
  const err = error as { code?: string; message?: string };
  if (err?.code !== PASSWORD_REQUIREMENTS_CODE || typeof err.message !== 'string') {
    return undefined;
  }
  const match = err.message.match(REQUIREMENTS_LIST_REGEX);
  const rawList = match?.[1];
  const items = rawList
    ? rawList.split(',').map((s) => s.trim()).filter(Boolean)
    : [];
  if (items.length === 0) {
    return 'Your password doesn\'t meet our security requirements. Please add uppercase and lowercase letters, numbers, and a symbol.';
  }
  const intro = 'Your password doesn\'t meet our requirements:';
  const bullets = items.map((item) => `• ${item}`).join('\n');
  return `${intro}\n${bullets}`;
}
